import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Form } from 'react-bootstrap';
import { Get, DeleteSingle } from '../../utils/httpProvider';
import { ANNUAL_JOB_API } from '../../utils/apiUrl';
import { toast } from 'react-toastify';
import Pagination from 'react-js-pagination';
import { useSelector, useDispatch } from 'react-redux';
import { resetSearch } from '@store/reducers/search';
import { useHistory, NavLink } from 'react-router-dom';
import { URI_INTERNAL } from '../../utils/pathLocations';
import { objectShowNumber } from '../../utils/define';

const AnnualJobCategoryList = () => {
    const history = useHistory();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const handleSort = (field) => {
        if (sortField === field) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortField(null);
                setSortDirection(null);
            } else {
                setSortDirection('asc');
            }
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const renderSortIcon = (field) => {
        if (sortField !== field) {
            return <i className="fas fa-sort text-muted ms-1" style={{ fontSize: '0.8rem' }}></i>;
        }
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up text-primary ms-1" style={{ fontSize: '0.8rem' }}></i>
            : <i className="fas fa-sort-down text-primary ms-1" style={{ fontSize: '0.8rem' }}></i>;
    };

    const sortedJobs = useMemo(() => {
        if (!sortField || !sortDirection) return jobs;
        return [...jobs].sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            if (typeof valA === 'string' && typeof valB === 'string') {
                return sortDirection === 'asc'
                    ? valA.localeCompare(valB, 'vi', { sensitivity: 'base' })
                    : valB.localeCompare(valA, 'vi', { sensitivity: 'base' });
            }

            if (valA === null || valA === undefined) valA = 0;
            if (valB === null || valB === undefined) valB = 0;
            return sortDirection === 'asc' ? valA - valB : valB - valA;
        });
    }, [jobs, sortField, sortDirection]);
    
    const dispatch = useDispatch();
    const valueSearch = useSelector((state) => state.search.valueSearch);

    const [filter, setFilter] = useState({ pageIndex: 1, pageSize: objectShowNumber[0].value, keyword: '' });
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        return () => { dispatch(resetSearch()); };
    }, [dispatch]);

    useEffect(() => {
        if (valueSearch != null && valueSearch !== filter.keyword) {
            setFilter(prev => ({ ...prev, keyword: valueSearch, pageIndex: 1 }));
        }
    }, [valueSearch]);

    useEffect(() => {
        fetchJobs();
    }, [filter.pageIndex, filter.pageSize, filter.keyword]);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const res = await Get(ANNUAL_JOB_API.GET_PAGING, null, filter);
            if (res) {
                const rawData = res.Data ?? res.data ?? res;
                const rawList = Array.isArray(rawData) ? rawData : (rawData.$values || []);
                const normalized = rawList.map(job => ({
                    id: job.Id ?? job.id,
                    name: job.Name ?? job.name,
                    jobGroupName: job.JobGroupName ?? job.jobGroupName,
                    unit: job.Unit ?? job.unit,
                    weight: job.Weight ?? job.weight,
                    baseQuantity: job.BaseQuantity ?? job.baseQuantity ?? 1
                }));
                setJobs(normalized);
                setTotalRecords(res.TotalRecords ?? res.totalRecords ?? 0);
            }
        } catch (error) {
            console.error('Error fetching jobs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa công việc này?')) {
            try {
                await DeleteSingle(`${ANNUAL_JOB_API.DELETE}/${id}`);
                toast.success('Xóa thành công');
                fetchJobs();
            } catch (error) {
                console.error('Error deleting job:', error);
                toast.error('Có lỗi xảy ra');
            }
        }
    };

    const handleEdit = (id) => {
        history.push(URI_INTERNAL.ANNUAL_JOB_CATEGORY_PATH_EDIT.replace(':id', id));
    };

    return (
        <div className="container-fluid">
            <Card>
                <Card.Header>
                    <h3 className="card-title">Danh mục công việc thường niên</h3>
                </Card.Header>
                <Card.Body style={{ minHeight: '600px', paddingBottom: '150px' }}>
                    <div className="d-flex justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                            <label className="mr-2 mb-0" style={{ whiteSpace: 'nowrap' }}>Hiển thị:</label>
                            <Form.Control
                                as="select"
                                size="sm"
                                style={{ width: '80px' }}
                                value={filter.pageSize}
                                onChange={(e) => {
                                    setFilter({ ...filter, pageSize: Number(e.target.value), pageIndex: 1 });
                                }}
                            >
                                {objectShowNumber.map((item, key) => (
                                    <option
                                        key={Number(key)}
                                        value={item.value}
                                    >
                                        {item.text}
                                    </option>
                                ))}
                            </Form.Control>

                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="Nhập tên công việc để tìm kiếm..."
                                style={{ width: '250px', marginLeft: '15px' }}
                                value={filter.keyword || ''}
                                onChange={(e) => setFilter({ ...filter, keyword: e.target.value, pageIndex: 1 })}
                            />
                        </div>
                        <NavLink to={URI_INTERNAL.ANNUAL_JOB_CATEGORY_PATH_ADD}>
                            <Button variant="info">
                                <i className="fas fa-plus"></i> Thêm mới
                            </Button>
                        </NavLink>
                    </div>

                <div className="table-responsive" style={{ overflow: 'visible' }}>
                    <Table striped bordered hover style={{ fontSize: '15px', tableLayout: 'fixed' }}>
                        <colgroup>
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '30%' }} />
                            <col style={{ width: '20%' }} />
                            <col style={{ width: '15%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                        </colgroup>
                        <thead className="bg-light">
                            <tr className="text-center">
                                <th style={{ fontSize: '16px' }}>STT</th>
                                <th style={{ fontSize: '16px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>
                                    Tên công việc {renderSortIcon('name')}
                                </th>
                                <th style={{ fontSize: '16px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('jobGroupName')}>
                                    Nhóm công việc {renderSortIcon('jobGroupName')}
                                </th>
                                <th style={{ fontSize: '16px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('unit')}>
                                    Đơn vị tính {renderSortIcon('unit')}
                                </th>
                                <th style={{ fontSize: '16px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('baseQuantity')}>
                                    Khối lượng chuẩn {renderSortIcon('baseQuantity')}
                                </th>
                                <th style={{ fontSize: '16px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('weight')}>
                                    Điểm {renderSortIcon('weight')}
                                </th>
                                <th style={{ fontSize: '16px' }}>Thao tác</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="text-center py-4">Đang tải...</td></tr>
                            ) : sortedJobs.length === 0 ? (
                                <tr><td colSpan="7" className="text-center py-4">Không có dữ liệu</td></tr>
                            ) : (
                                sortedJobs.map((job, index) => (
                                    <tr key={job.id}>
                                        <td className="text-center">{(filter.pageIndex - 1) * filter.pageSize + index + 1}</td>
                                        <td className="font-weight-bold">{job.name}</td>
                                        <td>{job.jobGroupName}</td>
                                        <td className="text-center">{job.unit}</td>
                                        <td className="text-center">{job.baseQuantity ?? job.BaseQuantity ?? 1}</td>
                                        <td className="text-center">{job.weight}</td>
                                        <td className="text-center">
                                            <Button variant="link" className="text-primary p-0 mr-3" title="Sửa" onClick={() => handleEdit(job.id)}>
                                                <i className="fas fa-edit fs-5"></i>
                                            </Button>
                                            <Button variant="link" className="text-danger p-0" title="Xóa" onClick={() => handleDelete(job.id)}>
                                                <i className="fas fa-trash fs-5"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>

                {totalRecords > filter.pageSize && (
                    <div className="d-flex justify-content-end mt-3">
                        <Pagination
                            activePage={filter.pageIndex}
                            itemsCountPerPage={filter.pageSize}
                            totalItemsCount={totalRecords}
                            pageRangeDisplayed={5}
                            onChange={(page) => setFilter({ ...filter, pageIndex: page })}
                            itemClass="page-item"
                            linkClass="page-link"
                        />
                    </div>
                )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AnnualJobCategoryList;
